import logger from './logger.js';
import { events, subscribe } from './eventManager.js';
let loading = false;
let requested = false;
let deferred = false;
export async function loadWebSdk(config) {
    const timeoutMs = config?.timeoutMs ?? 30000;
    if (!config || !/^[a-f0-9]{32}$/i.test(config.apiKey) || !['EU', 'US'].includes(config.region)
        || !Number.isFinite(timeoutMs) || timeoutMs <= 0 || timeoutMs > 2147483647
        || !config.integration || config.integration !== window.experimentIntegration
        || typeof config.connector?.isConsentGranted !== 'function'
        || typeof config.connector.getProjectApiKey !== 'function' || typeof config.connector.getUser !== 'function'
        || typeof config.consentAtPageLoad !== 'boolean'
        || (config.onError !== undefined && typeof config.onError !== 'function')) {
        const error = new Error('Web Experiment loader requires the installed integration, verified connector, page-load consent, project key, region and positive timeout');
        logger.error(error);
        throw error;
    }
    if (loading || requested || window.webExperiment || Array.from(document.scripts).some(script => /\.experiment\.js(?:[?#]|$)/.test(script.src))) {
        const error = new Error('Web Experiment runtime is already loading or installed; refusing a duplicate SDK');
        logger.error(error);
        throw error;
    }
    // Snapshot prior consent in the page head, not when the visitor clicks Accept.
    // A first grant may enable analytics, but must not start DOM experiments mid-page.
    if (!config.consentAtPageLoad) {
        deferred = true;
        logger.warn('Experiment deferred until a consented page load; original content is unchanged');
        return { status: 'deferred', dispose: () => config.integration.dispose() };
    }
    if (deferred) {
        const error = new Error('Experiment was deferred on this page; reload after consent instead of changing visible content');
        logger.error(error);
        throw error;
    }
    loading = true;
    try {
        await config.integration.setup();
        if (config.connector.isConsentGranted() !== true)
            throw new Error('Amplitude consent changed before SDK loading');
        if (config.connector.getProjectApiKey() !== config.apiKey)
            throw new Error('Experiment project does not match the verified Amplitude event destination');
        const user = config.integration.getUser();
        return await appendSdk(config, timeoutMs, user);
    }
    catch (error) {
        loading = false;
        logger.error(error);
        throw error;
    }
}
function appendSdk(config, timeoutMs, user) {
    const previousConfig = window.experimentConfig;
    const consentOptions = {
        ...previousConfig?.consentOptions,
        consentRequired: true,
        // Pending buffers writes/impressions without erasing a returning visitor's stored identity.
        consentStatus: 'pending',
    };
    const consentConfig = {
        ...previousConfig,
        consentOptions,
    };
    window.experimentConfig = consentConfig;
    const script = document.createElement('script');
    script.async = true;
    if (config.nonce !== undefined)
        script.nonce = config.nonce;
    const host = config.region === 'EU' ? 'cdn.eu.amplitude.com' : 'cdn.amplitude.com';
    script.src = `https://${host}/script/${config.apiKey}.experiment.js`;
    return new Promise((resolve, reject) => {
        let runtime;
        let disposed = false;
        let loaded = false;
        let lastConsent;
        let unsubscribe;
        function deny() {
            consentOptions.consentStatus = 'denied';
            const api = runtime ?? window.webExperiment;
            if (typeof api?.setConsentStatus === 'function')
                api.setConsentStatus('denied');
        }
        function cleanup() {
            if (interval !== undefined)
                clearInterval(interval);
            if (timeout !== undefined)
                clearTimeout(timeout);
            unsubscribe?.();
            script.onload = null;
            script.onerror = null;
            script.remove();
        }
        function fail(error) {
            const reason = error instanceof Error ? error : new Error(String(error));
            if (disposed)
                return;
            disposed = true;
            cleanup();
            loading = false;
            try {
                deny();
            }
            catch (denialError) {
                logger.error(denialError);
            }
            config.integration.dispose();
            logger.error(reason);
            if (config.onError) {
                try {
                    config.onError(reason);
                }
                catch (notificationError) {
                    logger.error(notificationError);
                }
            }
            if (!loaded)
                reject(reason);
        }
        function syncConsent() {
            if (disposed)
                return;
            try {
                if (config.integration.getState().status === 'disposed')
                    throw new Error('Experiment integration was disposed during SDK loading or execution');
                const allowed = config.connector.isConsentGranted() === true;
                if (allowed) {
                    if (config.connector.getProjectApiKey() !== config.apiKey)
                        throw new Error('Amplitude event destination changed; reload after verifying the project');
                    const current = config.connector.getUser();
                    if (!current || current.device_id !== user.device_id || current.user_id !== user.user_id) {
                        throw new Error('Experiment connector identity changed; reload before measuring a new identity');
                    }
                }
                consentOptions.consentStatus = allowed ? 'pending' : 'denied';
                if (!allowed && !runtime)
                    deny();
                if (runtime && allowed !== lastConsent) {
                    runtime.setConsentStatus(allowed ? 'granted' : 'denied');
                    lastConsent = allowed;
                }
            }
            catch (error) {
                fail(error);
            }
        }
        script.onload = () => {
            if (disposed)
                return;
            runtime = window.webExperiment;
            if (typeof runtime?.setConsentStatus !== 'function') {
                fail(new Error('Web Experiment runtime did not expose the supported consent API'));
                return;
            }
            syncConsent();
            if (disposed)
                return;
            loaded = true;
            if (timeout !== undefined)
                clearTimeout(timeout);
            resolve({
                status: 'loaded',
                dispose() {
                    if (disposed)
                        return;
                    try {
                        deny();
                    }
                    catch (error) {
                        logger.error(error);
                    }
                    disposed = true;
                    cleanup();
                    loading = false;
                    config.integration.dispose();
                },
            });
        };
        script.onerror = () => fail(new Error('Web Experiment SDK failed to load'));
        const interval = setInterval(syncConsent, 100);
        const timeout = setTimeout(() => fail(new Error('Timed out loading Web Experiment SDK')), timeoutMs);
        try {
            unsubscribe = subscribe(events.CONSENT, syncConsent);
            if (disposed) {
                unsubscribe();
                return;
            }
            // Removing a timed-out script cannot guarantee that it will never execute late.
            requested = true;
            document.head.appendChild(script);
        }
        catch (error) {
            fail(error);
        }
    });
}
